import { ReactNode } from 'react'

import { useAnalystHomePermission } from '@new/__global/hooks/useAnalystHomePermission'
import { useRevenueAgentsPermission } from '@new/__global/hooks/useRevenueAgentsPermission'
import { getDefaultCouponsRoute } from '@new/domains/marketing/utils/coupons'
import { useStayInboxPermission } from '@new/domains/operations/components/StayTimeline'
import { useTeamRosterPermission } from '@new/domains/operations/screens/Markets/useTeamRosterPermission'
import { viewModeOptions } from '@new/domains/properties/screens/viewMode/ViewMode.definitions'

import { mountRouteUrl, ROUTES } from '@routes'
import { Features } from '@utils/optimizely'

export interface RouteItemProps {
  label: string
  value: string
  externalUrl?: string
  feature?: Features
  labelComponent?: ReactNode
}

export const useRouteMenu = (key?: string): RouteItemProps[] => {
  const hasRevenueAgentsPermission = useRevenueAgentsPermission()
  const hasAnalystHomePermission = useAnalystHomePermission()
  const { canReadTeamRoster } = useTeamRosterPermission()
  const { canReadStayInbox } = useStayInboxPermission()

  const mainMenuItems = Object.values(ROUTES)
    .filter(menuItem => menuItem.navHeader)
    .map(item => ({
      label: item.title,
      value: item.path,
    }))

  const bookings: RouteItemProps[] = [
    {
      label: 'Tape Chart',
      value: ROUTES.fastTapeChart.path,
    },
    {
      label: 'Hotels Tape Chart',
      value: ROUTES.hotelTapeChart.path,
    },
    { label: 'All Bookings', value: ROUTES.bookings.path },
    { label: 'Groups & Events', value: ROUTES.groupsEvents.path },
    {
      label: 'Booking Conflicts',
      value: ROUTES.bookingConflicts.path,
    },
    {
      label: 'ID Verification',
      value: ROUTES.idVerification.path,
    },
    {
      label: 'Requests',
      value: ROUTES.bookingServicesRequests.path,
      feature: Features.ValueAddedServicesRequests,
    },
  ]

  const ratings = [
    { label: 'Performance', value: ROUTES.ratingsPerformance.path },
    { label: 'NPS by Channel', value: ROUTES.ratingsNpsByChannel.path },
    { label: 'Review Management', value: ROUTES.ratingsReviewsManagement.path },
    { label: 'Airbnb Quality', value: ROUTES.ratingsAirbnbQuality.path },
    { label: 'Superhost', value: ROUTES.ratingsSuperhost.path },
  ]

  const people = [
    { label: ROUTES.guests.title, value: mountRouteUrl(ROUTES.guests.path) },
    { label: ROUTES.homeowners.title, value: mountRouteUrl(ROUTES.homeowners.path) },
    { label: ROUTES.lighthouseAccess.title, value: mountRouteUrl(ROUTES.lighthouseAccess.path) },
    { label: ROUTES.bookingLeads.title, value: mountRouteUrl(ROUTES.bookingLeads.path) },
    { label: ROUTES.marketAssignments.title, value: ROUTES.marketAssignments.path },
  ]

  const revenue: RouteItemProps[] = [
    { label: ROUTES.taxes.title, value: ROUTES.taxes.path },
    { label: ROUTES.fees.title, value: ROUTES.fees.path },
    {
      label: ROUTES.pricingTapeChart.title,
      value: ROUTES.pricingTapeChart.path,
    },
    {
      label: ROUTES.revenueBookingsTapeChart.title,
      value: ROUTES.revenueBookingsTapeChart.path,
    },
    {
      label: ROUTES.pricingTool.title,
      value: ROUTES.pricingTool.path,
    },
    ...(hasAnalystHomePermission
      ? [
          {
            label: ROUTES.revenueAnalystHome.title,
            value: ROUTES.revenueAnalystHome.path,
          },
        ]
      : []),
    ...(hasRevenueAgentsPermission
      ? [
          {
            label: 'Agents',
            value: ROUTES.revenueAgents.path,
          },
        ]
      : []),
    {
      label: ROUTES.underwriting.title,
      value: ROUTES.underwriting.path,
    },
    {
      label: ROUTES.revenueTickets.title,
      value: ROUTES.revenueTickets.path,
    },
    {
      label: ROUTES.accountingResolutionCenter.title,
      value: ROUTES.accountingResolutionCenter.path,
    },
    {
      label: ROUTES.ownerIncomeDashboard.title,
      value: ROUTES.ownerIncomeDashboard.path,
    },
    {
      label: ROUTES.revenueMapping.title,
      value: ROUTES.revenueMapping.path,
    },
    {
      label: ROUTES.seasonGroups.title,
      value: ROUTES.seasonGroups.path,
    },
    {
      label: ROUTES.discountTapeChart.title,
      value: ROUTES.discountTapeChart.path,
    },
  ]

  const operations: RouteItemProps[] = [
    { label: 'Cases', value: ROUTES.operationCases.path },
    { label: 'Cases Tape Chart', value: ROUTES.operationCasesTapeChart.path },
    ...(canReadStayInbox
      ? [{ label: ROUTES.operationGuestInbox.title, value: ROUTES.operationGuestInbox.path }]
      : []),
    { label: 'Expenses', value: ROUTES.operationExpenses.path },
    { label: 'Cleans & Inspections', value: ROUTES.operationWorkOrder.path },
    ...(canReadTeamRoster ? [{ label: 'Market Teams', value: ROUTES.operationMarkets.path }] : []),
    { label: 'Vendors', value: ROUTES.operationVendors.path },
    { label: 'Vendor Email Invoices', value: ROUTES.operationVendorEmailInvoices.path },
  ]

  const marketing = [
    {
      label: 'Discount Coupons',
      value: getDefaultCouponsRoute(),
      feature: Features.Coupons,
    },
    { label: 'Quotes', value: ROUTES.quotes.path, feature: Features.Quotes },
    {
      label: 'Quotes History',
      value: ROUTES.quotesHistory.path,
      feature: Features.Quotes,
    },
    { label: 'VRM Coupons', value: ROUTES.vrmCoupons.path },
  ]

  const markets = [
    { label: ROUTES.marketsDailyAgenda.title, value: ROUTES.marketsDailyAgenda.path },
    {
      label: ROUTES.marketsTaskManagement.title,
      value: ROUTES.marketsTaskManagement.path,
    },
    {
      label: ROUTES.marketsIotOperations.title,
      value: ROUTES.marketsIotOperations.path,
    },
  ]

  const finance: RouteItemProps[] = [
    { label: ROUTES.unitEconomics.title, value: ROUTES.unitEconomics.path },
    { label: ROUTES.netsuitePayables.title, value: ROUTES.netsuitePayables.path },
    {
      label: 'FP&A Dashboard',
      value: 'fpna-dashboard',
      externalUrl: 'https://fpna.prod.avantstay.com/',
    },
  ]

  const listingDraft = [
    { label: ROUTES.allListingDrafts.title, value: ROUTES.allListingDrafts.path },
  ]

  const buildings = [
    { label: ROUTES.buildingsList.title, value: ROUTES.buildingsList.path },
    { label: ROUTES.performanceHub.title, value: ROUTES.performanceHub.path },
  ]

  const allSubMenus = {
    properties: viewModeOptions,
    bookings,
    // Since fastTapeChart we need to map the field so that we may build the mobile menu
    fastTapeChart: bookings,
    people,
    ratings,
    operations,
    marketing,
    markets,
    finance,
    revenue,
    listingDraft,
    buildings,
  }

  if (key) {
    const menuEntries = Object.entries(allSubMenus)
    const filteredMenuFromKey = menuEntries.filter(item => item[0].includes(key))
    return filteredMenuFromKey.map(menuItem => menuItem[1]).flat()
  }

  return mainMenuItems
}
